(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('react')) : typeof define === 'function' && define.amd ? define(['exports', 'react'], factory) : (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.ReactCalendarToolkit = {}, global.React));
})(this, function (exports, react) {
  'use strict';
  /** @function
   * @name setCSSVariable
   * @description Set CSS variable
   * @param {MutableRefObject<null>} ref - React ref
   * @return {function(string, string): void}
   */

  var setCSSVariable = function setCSSVariable(ref) {
    return function (variableName, value) {
      var element = ref.current;
      element && element.style.setProperty("--".concat(variableName), value);
    };
  };
  /** @function
   * @name removeCSSVariable
   * @description Remove CSS variable
   * @param {MutableRefObject<null>} ref - React ref
   * @return {function(string): void}
   */


  var removeCSSVariable = function removeCSSVariable(ref) {
    return function (variableName) {
      var element = ref.current;
      element && element.style.removeProperty("--".concat(variableName));
    };
  };
  /** @function
   * @name getCSSVariable
   * @description Get CSS variable value
   * @param {MutableRefObject<null>} ref - React ref
   * @return {function(string): string}
   */


  var getCSSVariable = function getCSSVariable(ref) {
    return function (variableName) {
      var element = ref.current;
      return element && element.style.getPropertyValue("--".concat(variableName));
    };
  };
  /** @function
   * @name createStyleObject
   * @description Adds `--` prefix to property names in theme object
   * @param {Object<string, string>} theme - Theme object
   * @return {Object<string, string>}
   */


  var createStyleObject = function createStyleObject(theme) {
    var keys = Object.keys(theme);
    var result = {};
    keys.forEach(function (key) {
      // eslint-disable-next-line fp/no-mutation
      result["--".concat(key)] = theme[key];
    });
    return result;
  };
  /**
   * @typedef {Object} UseThemeInterface
   * @property {MutableRefObject<null>} ref - React ref. Use as theme container element getter only.
   * @property {function(HTMLElement): void} setRef - Theme container element setter.
   * @property {Object<string, string>} style - Theme object with all property names prefixed `--`.
   * @property {function(string): string} getVariable - Get variable value. getCSSVariable with bound ref.
   * @see src/lib/utils.js
   * @property {function(variableName: string, value: (string|number)): void} setVariable - Set variable value. setCSSVariable with bound ref.
   * @property {function(variableName: string): string} getVariable - Get variable value. getCSSVariable with bound ref.
   * @property {function(variableName: string): string} removeVariable - Remove variable. removeCSSVariable with bound ref.
   */

  /** @function
   * @name useTheme
   * @description React hook to apply multiple CSS variables and manipulate them.
   * @param {Object<string, string>} theme - React ref
   * @return {UseThemeInterface}
   */


  var useTheme = function useTheme(theme) {
    var ref = react.useRef(null);
    var setVariable = setCSSVariable(ref);
    var getVariable = getCSSVariable(ref);
    var removeVariable = removeCSSVariable(ref);
    var style = createStyleObject(theme);
    var setRef = react.useCallback(function (element) {
      if (ref.current) ;

      if (element) {
        Object.keys(theme).forEach(function (key) {
          var isEqual = getCSSVariable({
            current: element
          })(key) === theme[key];
          !isEqual && setCSSVariable({
            current: element
          })(key, theme[key]);
        });
      } // Save a reference to the node


      ref.current = element;
    }, [theme]);
    return {
      ref: ref,
      setRef: setRef,
      style: style,
      setVariable: setVariable,
      getVariable: getVariable,
      removeVariable: removeVariable
    };
  };

  function _defineProperty(obj, key, value) {
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }

    return obj;
  }
  /**
   * @typedef {Object} UseThemeInterface
   * @property {MutableRefObject<null>} ref - React ref. Use as theme container element getter only.
   * @property {function(HTMLElement): void} setRef - Theme container element setter.
   * @property {Object<string, string>} style - Theme object with all property names prefixed `--`.
   * @property {function(string): string} getVariable - Get variable value. getCSSVariable with bound ref.
   * @see src/lib/utils.js
   * @property {function(variableName: string, value: (string|number)): void} setVariable - Set variable value. setCSSVariable with bound ref.
   * @property {function(variableName: string): string} getVariable - Get variable value. getCSSVariable with bound ref.
   * @property {function(variableName: string): string} removeVariable - Remove variable. removeCSSVariable with bound ref.
   */

  /** @function
   * @name useTheme
   * @description React hook to apply multiple CSS variables and manipulate them.
   * @param {string} name - Name of the variable, without `--`.
   * @param {string|number} value - value of the variable.
   * @return {UseThemeInterface}
   */


  var useVariable = function useVariable(name, value) {
    var _useTheme = useTheme(_defineProperty({}, name, value)),
        ref = _useTheme.ref,
        setRef = _useTheme.setRef,
        style = _useTheme.style,
        setVariable = _useTheme.setVariable,
        getVariable = _useTheme.getVariable,
        removeVariable = _useTheme.removeVariable;

    return {
      ref: ref,
      setRef: setRef,
      style: style,
      setVariable: setVariable,
      getVariable: getVariable,
      removeVariable: removeVariable
    };
  };

  exports.useTheme = useTheme;
  exports.useVariable = useVariable;
  Object.defineProperty(exports, '__esModule', {
    value: true
  });
});